import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Eye,
  Plus,
  Send,
  Edit,
  ExternalLink,
  User,
  Mail,
  Shield,
  UserPlus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2
} from 'lucide-react';
import { useClientPortal } from '@/hooks/useClientPortal';
import { toast } from 'sonner';
import { FeedbackThread } from './FeedbackThread';
import { EventEditDialog } from './EventEditDialog';
import { supabase } from '@/integrations/supabase/client';

interface ClientPortalProps {
  clientId: string;
  sessionToken?: string;
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  joined_at: string | null;
}

export function ClientPortal({ clientId, sessionToken }: ClientPortalProps) {
  const navigate = useNavigate();
  const { 
    events, 
    approvals, 
    feedback, 
    loading, 
    updateApprovalStatus,
    submitFeedback,
    refetch
  } = useClientPortal(clientId);

  const [newFeedback, setNewFeedback] = useState({
    eventId: '',
    type: 'general' as const,
    message: '',
    priority: 'medium' as const
  });

  const [approvalNotes, setApprovalNotes] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [clientName, setClientName] = useState<string>('');
  
  // Team management state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'member' });
  const [inviting, setInviting] = useState(false);
  const [togglingMember, setTogglingMember] = useState<string | null>(null);

  // Fetch client name
  useEffect(() => {
    const fetchClientName = async () => {
      const { data } = await supabase
        .from('clients')
        .select('name')
        .eq('id', clientId)
        .single();
      if (data) setClientName(data.name);
    };
    if (clientId) fetchClientName();
  }, [clientId]);

  // Load team members with real-time subscription
  const loadTeamMembers = async () => {
    if (!clientId) return;
    
    try {
      setLoadingTeam(true);
      const { data, error } = await supabase
        .from('client_team_members')
        .select('*')
        .eq('client_id', clientId)
        .order('joined_at', { ascending: false });

      if (error) throw error;
      setTeamMembers(data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoadingTeam(false);
    }
  };

  useEffect(() => {
    loadTeamMembers();
  }, [clientId]);

  // Real-time subscription for team members
  useEffect(() => {
    if (!clientId) return;

    const channel = supabase
      .channel(`client-team-${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_team_members',
          filter: `client_id=eq.${clientId}`
        },
        () => loadTeamMembers()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId]);

  // Team management functions
  const handleInviteTeamMember = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setInviting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create invitation
      const { error } = await supabase
        .from('client_team_invitations')
        .insert({
          client_id: clientId,
          name: inviteForm.name,
          email: inviteForm.email.toLowerCase(),
          role: inviteForm.role,
          invited_by: user.id,
        });

      if (error) throw error;

      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInviteDialog(false);
      setInviteForm({ name: '', email: '', role: 'member' });
    } catch (error: any) {
      console.error('Error inviting team member:', error);
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleToggleMemberStatus = async (memberId: string, currentStatus: boolean, memberEmail: string, memberName: string, clientName: string) => {
    setTogglingMember(memberId);
    try {
      const { error } = await supabase
        .from('client_team_members')
        .update({ is_active: !currentStatus })
        .eq('id', memberId);

      if (error) throw error;
      const newStatus = currentStatus ? 'deactivated' : 'activated';
      toast.success(`Team member ${newStatus}`);
      
      // Send email notification
      await supabase.functions.invoke('send-team-status-notification', {
        body: {
          email: memberEmail,
          name: memberName,
          clientName,
          status: newStatus
        }
      });
      
      loadTeamMembers();
    } catch (error) {
      console.error('Error toggling member status:', error);
      toast.error('Failed to update member status');
    } finally {
      setTogglingMember(null);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;

    try {
      const { error } = await supabase
        .from('client_team_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
      toast.success('Team member removed');
      loadTeamMembers();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove team member');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: { variant: 'outline' as const, icon: Clock, color: 'text-yellow-600' },
      approved: { variant: 'default' as const, icon: CheckCircle, color: 'text-green-600' },
      rejected: { variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' },
      needs_changes: { variant: 'secondary' as const, icon: AlertCircle, color: 'text-orange-600' }
    };
    
    const config = variants[status as keyof typeof variants] || variants.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const handleApprovalAction = async (approvalId: string, status: 'approved' | 'rejected' | 'needs_changes') => {
    try {
      await updateApprovalStatus(approvalId, status, approvalNotes);
      toast.success(`Event ${status} successfully`);
      setApprovalNotes('');
    } catch (error) {
      toast.error('Failed to update approval status');
    }
  };

  const handleSubmitFeedback = async () => {
    if (!newFeedback.eventId || !newFeedback.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await submitFeedback(
        newFeedback.eventId,
        newFeedback.type,
        newFeedback.message,
        newFeedback.priority
      );
      toast.success('Feedback submitted successfully');
      setNewFeedback({ eventId: '', type: 'general', message: '', priority: 'medium' });
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <p>Loading your portal...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <img 
            src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
            alt="Event Date Picker Logo" 
            className="h-10 w-auto" 
          />
          <div>
            <h1 className="text-3xl font-bold">Client Portal</h1>
            <p className="text-muted-foreground">Manage your events, team, and provide feedback</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="events" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="team">
            Team
            {teamMembers.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {teamMembers.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approvals">
            Approvals
            {approvals.filter(a => a.status === 'pending').length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {approvals.filter(a => a.status === 'pending').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Your Events</h2>
          </div>
          
          <div className="grid gap-4">
            {events.map((event) => (
              <Card key={event.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{event.name}</CardTitle>
                    <div className="flex items-center gap-2">
                      {event.event_type === 'rsvp' && (
                        <Badge variant="outline">RSVP Event</Badge>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/event/${event.id}/manage`)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Manage
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(`/vote/${event.shareable_link}`, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View & Vote
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" onClick={() => setSelectedEvent(event)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Details
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{event.name}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <p className="text-muted-foreground">{event.description}</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {event.rsvp_date && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{event.rsvp_date}</span>
                                </div>
                              )}
                              {event.rsvp_time && (
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{event.rsvp_time}</span>
                                </div>
                              )}
                              {event.rsvp_location && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{event.rsvp_location}</span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{event.guests?.length || 0} guests invited</span>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">{event.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {event.guests?.length || 0} guests
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(event.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {events.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No events found</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Team Members</h2>
            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Team Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Team Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="invite-name">Name</Label>
                    <Input
                      id="invite-name"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                      placeholder="Enter name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      placeholder="Enter email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="invite-role">Role</Label>
                    <Select value={inviteForm.role} onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleInviteTeamMember} disabled={inviting} className="w-full">
                    {inviting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Invitation
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="grid gap-4">
            {loadingTeam ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p className="text-muted-foreground">Loading team members...</p>
                </CardContent>
              </Card>
            ) : teamMembers.length > 0 ? (
              teamMembers.map((member) => (
                <Card key={member.id} className={!member.is_active ? 'opacity-60' : ''}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${member.is_active ? 'bg-primary/10' : 'bg-muted'}`}>
                          <User className={`h-5 w-5 ${member.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail className="h-3 w-3" />
                            {member.email}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={member.is_active ? 'outline' : 'secondary'} className="capitalize">
                          <Shield className="h-3 w-3 mr-1" />
                          {member.role}
                        </Badge>
                        {!member.is_active && (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleMemberStatus(member.id, member.is_active, member.email, member.name, clientName)}
                          disabled={togglingMember === member.id}
                        >
                          {togglingMember === member.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : member.is_active ? (
                            <ToggleRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMember(member.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No team members yet</p>
                  <p className="text-sm text-muted-foreground mb-4">Add team members to collaborate on your events</p>
                  <Button onClick={() => setShowInviteDialog(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Your First Team Member
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Approvals Tab */}
        <TabsContent value="approvals" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Event Approvals</h2>
          </div>
          
          <div className="grid gap-4">
            {approvals.map((approval) => (
              <Card key={approval.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {events.find(e => e.id === approval.event_id)?.name || 'Event'}
                    </CardTitle>
                    {getStatusBadge(approval.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Requested on {new Date(approval.requested_at).toLocaleDateString()}
                  </p>
                  
                  {approval.approval_notes && (
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm">{approval.approval_notes}</p>
                    </div>
                  )}

                  {approval.status === 'pending' && (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="approval-notes">Notes (optional)</Label>
                        <Textarea
                          id="approval-notes"
                          placeholder="Add any notes about this approval..."
                          value={approvalNotes}
                          onChange={(e) => setApprovalNotes(e.target.value)}
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleApprovalAction(approval.id, 'approved')}
                          className="flex-1"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => handleApprovalAction(approval.id, 'needs_changes')}
                          className="flex-1"
                        >
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Request Changes
                        </Button>
                        <Button 
                          variant="destructive"
                          onClick={() => handleApprovalAction(approval.id, 'rejected')}
                          className="flex-1"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {approvals.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No approval requests found</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Feedback Tab - Updated with Thread Support */}
        <TabsContent value="feedback" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Feedback & Communication</h2>
            <Dialog>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Feedback
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Submit Feedback</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="event-select">Event</Label>
                    <Select value={newFeedback.eventId} onValueChange={(value) => 
                      setNewFeedback(prev => ({ ...prev, eventId: value }))
                    }>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an event" />
                      </SelectTrigger>
                      <SelectContent>
                        {events.map((event) => (
                          <SelectItem key={event.id} value={event.id}>
                            {event.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="feedback-type">Type</Label>
                    <Select value={newFeedback.type} onValueChange={(value: any) => 
                      setNewFeedback(prev => ({ ...prev, type: value }))
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Feedback</SelectItem>
                        <SelectItem value="revision_request">Revision Request</SelectItem>
                        <SelectItem value="question">Question</SelectItem>
                        <SelectItem value="approval">Approval Comment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={newFeedback.priority} onValueChange={(value: any) => 
                      setNewFeedback(prev => ({ ...prev, priority: value }))
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Enter your feedback..."
                      value={newFeedback.message}
                      onChange={(e) => setNewFeedback(prev => ({ ...prev, message: e.target.value }))}
                      rows={4}
                    />
                  </div>

                  <Button onClick={handleSubmitFeedback} className="w-full">
                    <Send className="h-4 w-4 mr-2" />
                    Submit Feedback
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="grid gap-4">
            {feedback.map((item) => (
              <Card key={item.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setSelectedFeedback(item)}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {events.find(e => e.id === item.event_id)?.name || 'Event'}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant={item.priority === 'urgent' ? 'destructive' : 'outline'}>
                        {item.priority.toUpperCase()}
                      </Badge>
                      <Badge variant={item.status === 'open' ? 'default' : 'secondary'}>
                        {item.status.toUpperCase()}
                      </Badge>
                      <Button variant="ghost" size="sm">
                        <MessageSquare className="h-4 w-4 mr-1" />
                        View Thread
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2">{item.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {item.feedback_type.replace('_', ' ').toUpperCase()} • 
                    Submitted on {new Date(item.created_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
            
            {feedback.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No feedback submitted yet</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Feedback Thread Dialog */}
          <Dialog open={!!selectedFeedback} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh]">
              {selectedFeedback && (
                <FeedbackThread
                  feedbackId={selectedFeedback.id}
                  feedbackMessage={selectedFeedback.message}
                  feedbackType={selectedFeedback.feedback_type}
                  feedbackPriority={selectedFeedback.priority}
                  feedbackStatus={selectedFeedback.status}
                  eventName={events.find(e => e.id === selectedFeedback.event_id)?.name || 'Event'}
                  userRole="client"
                  authorName="You"
                  createdAt={selectedFeedback.created_at}
                  canReply={true}
                />
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <h2 className="text-xl font-semibold">Portal Overview</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{events.length}</div>
                <p className="text-xs text-muted-foreground">Total Events</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{teamMembers.length}</div>
                <p className="text-xs text-muted-foreground">Team Members</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-yellow-600">
                  {approvals.filter(a => a.status === 'pending').length}
                </div>
                <p className="text-xs text-muted-foreground">Pending Approvals</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-blue-600">
                  {feedback.filter(f => f.status === 'open').length}
                </div>
                <p className="text-xs text-muted-foreground">Open Feedback</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-green-600">
                  {approvals.filter(a => a.status === 'approved').length}
                </div>
                <p className="text-xs text-muted-foreground">Approved Events</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Event Edit Dialog */}
      {editingEvent && (
        <EventEditDialog
          event={editingEvent}
          open={!!editingEvent}
          onOpenChange={(open) => !open && setEditingEvent(null)}
          onSave={refetch}
        />
      )}
    </div>
  );
}
